<script setup lang="ts">
import { computed } from 'vue'
import { twMerge } from 'tailwind-merge'
import { MULTIPLIER_MIN_SPINS, getHighestAffordableMultiplier, getMultiplierOptions } from '@/composables/game/fortune-wheel'

const props = withDefaults(defineProps<{
  availableSpins: number
  modelValue: number
  disabled?: boolean
  isPressed?: boolean
}>(), {
  disabled: false,
  isPressed: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const multiplierOptions = computed(() => getMultiplierOptions(props.availableSpins))

const showSelector = computed(() => props.availableSpins >= MULTIPLIER_MIN_SPINS)

const highestAvailable = computed(() => getHighestAffordableMultiplier(props.availableSpins))

const isMaxMultiplier = computed(() => {
  return props.modelValue === highestAvailable.value && props.modelValue > 1
})

const currentIndex = computed(() => {
  return multiplierOptions.value.indexOf(props.modelValue)
})

function cycleMultiplier() {
  if (props.disabled) return

  const options = multiplierOptions.value
  let nextIndex = (currentIndex.value + 1) % options.length
  let nextValue = options[nextIndex]

  // Skip options that require more spins than available
  let attempts = 0
  while (props.availableSpins < nextValue && attempts < options.length) {
    nextIndex = (nextIndex + 1) % options.length
    nextValue = options[nextIndex]
    attempts++
  }

  emit('update:modelValue', nextValue)
}
</script>
<template>
  <button
    v-if="showSelector"
    :class="twMerge(
      'multiplier-btn size-[44px] rounded-full border-[3px] border-red-accent overflow-hidden box-content',
      disabled && 'disabled-button text-white[.38]',
    )"
    @click.stop="cycleMultiplier"
  >
    <span
      :class="twMerge(
        'multiplier-btn-inner flex items-center justify-center w-full h-full rounded-full font-bold text-white/[.87] text-lg transition-all duration-150',
        isMaxMultiplier
          ? 'multiplier-btn-max'
          : 'multiplier-btn-default',
        isPressed && !isMaxMultiplier && 'multiplier-btn-pressed',
        isPressed && isMaxMultiplier && 'multiplier-btn-max-pressed',
      )"
    >
      <span v-if="isMaxMultiplier" class="text-xs font-bold">MAX</span>
      <span v-else>x{{ modelValue }}</span>
    </span>
  </button>
</template>
<style scoped>
.multiplier-btn {
  padding: 0;
}

.multiplier-btn-inner {
  position: relative;
  margin-top: -2px;
  transition: background 0.15s ease, box-shadow 0.15s ease, margin-top 0.1s ease;
}

.multiplier-btn-default {
  background: #FFA500;
  box-shadow: 0 4px 0 0 #996300;
}

.multiplier-btn-pressed,
.multiplier-btn:active .multiplier-btn-default {
  background: #BF7E08;
  box-shadow: inset 0 0 0 3px #FFA500;
  margin-top: 0;
}

.multiplier-btn-max {
  background: #A138B3;
  box-shadow: 0 4px 0 0 #641372;
}

.multiplier-btn-max-pressed,
.multiplier-btn:active .multiplier-btn-max {
  background: #7A2A8A;
  box-shadow: inset 0 0 0 3px #B050C8;
  margin-top: 0;
}
</style>
